import {assert} from 'Asserts';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedureRunPlanConditionREST} from 'procedures/network/procedureRunPlanDAO';
import {deriveConfirmationMethodByParamVersionId} from 'procedures/planning/buildFullExperimentPlan';
import {
  expandPlanDataParameterValuesForRest,
  expandPlanMaterialInputValuesForRest,
} from 'procedures/planning/multiValuePlateMap';
import {
  ExperimentalInputEvaluatedValues,
  ExperimentalInputs,
  ExperimentalInputsPerMethod,
  ExperimentalInputTypedValuesByComponentName,
} from 'procedures/planning/ProcedurePlanTypes';
import {PlanConditionsByMethodId} from 'studies/typed/shared/procedure-runs/design-state/planConditionState';

const MATERIAL_INDEX = 0;
const VALUE_INDEX = 0;
const AMOUNT_INDEX = 1;

interface SerializePlanConditionsArgs {
  planConditionsByMethodId: PlanConditionsByMethodId;
  /** Flowchart method order. Each method's rows are emitted before the next method. */
  methodIdsInOrder: ReadonlyArray<string>;
  experimentalInputs: ExperimentalInputs;
  evaluatedValues: ExperimentalInputEvaluatedValues;
}

/**
 * Serializes per-method plan rows to the procedure run plan REST format.
 *
 * `position` is the row's dense array index within its method.
 */
export function serializePlanConditionsByMethodForNetwork({
  planConditionsByMethodId,
  methodIdsInOrder,
  experimentalInputs,
  evaluatedValues,
}: SerializePlanConditionsArgs): Array<ProcedureRunPlanConditionREST> {
  const confirmationMethodByParamVersionId = deriveConfirmationMethodByParamVersionId(experimentalInputs);
  const payloads: Array<ProcedureRunPlanConditionREST> = [];
  methodIdsInOrder.forEach((methodId) => {
    const rows = planConditionsByMethodId[methodId] ?? [];
    rows.forEach((row, position) => {
      const {id, name, description, numReplicates} = row;
      const inputs = experimentalInputs[methodId];
      assert(inputs != null, `Expected experimental inputs for method ${methodId}`);
      payloads.push({
        ...(id != null ? {id} : {}),
        templateMethodVersionId: methodId,
        name,
        description,
        position,
        replicateInfos: Array.from({length: numReplicates}, (_, replicateIndex) => ({
          replicateNumber: replicateIndex + 1,
        })),
        methodPlan: buildPlanMethodPlanRest(
          methodId,
          inputs,
          evaluatedValues[methodId]?.values[position],
          confirmationMethodByParamVersionId
        ),
      });
    });
  });
  return payloads;
}

/**
 * Builds one method plan from a method's parameter ids and a single condition's evaluated cells.
 */
export function buildPlanMethodPlanRest(
  methodId: string,
  inputsForMethod: ExperimentalInputsPerMethod,
  conditionValues: ExperimentalInputTypedValuesByComponentName | undefined,
  confirmationMethodByParamVersionId: Record<string, ProcedureParameterConfirmationMethod>
): NonNullable<ProcedureRunPlanConditionREST['methodPlan']> {
  return {
    methodVersionId: methodId,
    dataParameterValues: inputsForMethod.ids.parameters.map((paramVersionId, paramIdx) => ({
      dataParameterVersionId: paramVersionId,
      values: expandPlanDataParameterValuesForRest(conditionValues?.parameters[paramIdx]?.[VALUE_INDEX]),
    })),
    materialInputValues: inputsForMethod.ids.materialInputs.map((materialVersionId, materialIdx) => ({
      materialInputVersionId: materialVersionId,
      values: expandPlanMaterialInputValuesForRest(
        conditionValues?.materialInputs[materialIdx]?.[MATERIAL_INDEX],
        conditionValues?.materialInputs[materialIdx]?.[AMOUNT_INDEX],
        confirmationMethodByParamVersionId[materialVersionId]
      ),
    })),
    equipmentValues: inputsForMethod.ids.equipment.map((equipmentVersionId, equipmentIdx) => ({
      equipmentVersionId,
      // Equipment parameters have allowMultiValueForPlateMap: false, so always a single value
      // entry. Coerce undefined to null so an unset cell serializes as an explicit null rather
      // than a missing key.
      values: [
        {
          value: {
            equipment: conditionValues?.equipment[equipmentIdx]?.[VALUE_INDEX] ?? null,
          },
        },
      ],
    })),
  };
}
